import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AdminKanban } from "@sd/components/admin/AdminKanban";
import { AdminProjectStatsDashboard } from "@sd/components/admin/AdminProjectStatsDashboard";
import { TrackerReportDashboard } from "@sd/components/reports/TrackerReportDashboard";
import { AgentTicketWorkbench } from "@sd/components/tickets/AgentTicketWorkbench";
import { TicketsStreamGrid } from "@sd/components/tickets/TicketsStreamGrid";
import { CommandPalette } from "@sd/components/CommandPalette";
import { Header } from "@sd/components/layout/Header";
import { Sidebar } from "@sd/components/layout/Sidebar";
import { mapTicketsQueueToAdminPath, sd } from "@sd/lib/sdPaths";
import { isAgentAllowedTicketQueue } from "@sd/lib/sdAgentRouteAccess";
import { SdNotFound } from "@sd/components/SdNotFound";
import { useTicketsPage } from "@sd/hooks/useTicketsPage";
import { useAgentCompactLayout } from "@sd/hooks/use-mobile";
import { useAuth } from "@sd/hooks/useAuth";
import { getUserInfo } from "@/lib/storeManager";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const Tickets = () => {
  const { queue: queueParam } = useParams<{ queue?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSupportAgent, isSuperAdmin, isLoading: authLoading, role } =
    useAuth();
  const isSuperAgent = role === "ServiceDesk_SuperAgent";
  const isAgentOnly = role === "ServiceDesk_Agent";
  const queue =
    queueParam ??
    (isSupportAgent ? "project-status" : "all");
  const vm = useTicketsPage(queue);
  const isCompact = useAgentCompactLayout();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const kanbanEmpId = vm.isAgentOnly
    ? String((getUserInfo() as { id?: string } | null)?.id ?? "")
    : undefined;

  const sidebarProps = {
    collapsed: sidebarCollapsed,
    onToggle: () => setSidebarCollapsed(c => !c),
    activeQueue: queue,
    onQueueChange: () => {},
    activeTab: "tickets" as const,
    onTabChange: (tab: "tickets" | "assets") => {
      if (tab === "assets") navigate(sd(`assets/${queue}`));
    },
    queueCounts: vm.sidebarQueueCounts,
  };

  const useTicketShell = isSupportAgent || isSuperAdmin;

  useEffect(() => {
    if (role === "ServiceDesk_Admin") {
      const target = mapTicketsQueueToAdminPath(queueParam);
      const next = `${target}${location.search}`;
      if (`${location.pathname}${location.search}` !== next) {
        navigate(next, { replace: true });
      }
      return;
    }
    if (role === "ServiceDesk_Agent" && !queueParam) {
      navigate(`${sd("tickets/project-status")}${location.search}`, { replace: true });
      return;
    }
    if (queue === "pending" || queue === "resolved") {
      navigate(sd("tickets/my-tickets"), { replace: true });
      return;
    }
  }, [queue, role, isSuperAgent, isSuperAdmin, navigate, queueParam, location.pathname, location.search]);

  if (
    isAgentOnly &&
    queueParam &&
    !isAgentAllowedTicketQueue(queueParam)
  ) {
    return <SdNotFound />;
  }

  if (role === "ServiceDesk_Admin") {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (authLoading && !role) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (useTicketShell) {
    const mainContent =
      queue === "report" ? (
        <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-background">
          <TrackerReportDashboard panel="agent" disableDrillDown={isAgentOnly} />
        </div>
      ) : queue === "project-status" ? (
        <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-background">
          <AdminProjectStatsDashboard
            panel="agent"
            disableStatusDrillDown={isAgentOnly}
          />
        </div>
      ) : queue === "kanban" ? (
        <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
          <AdminKanban empId={kanbanEmpId} enableFilters />
        </div>
      ) : isSupportAgent ? (
        <AgentTicketWorkbench vm={vm} isAgentOnly={vm.isAgentOnly} />
      ) : (
        <TicketsStreamGrid
          queue={queue}
          vm={vm}
          isAgentOnly={vm.isAgentOnly}
        />
      );

    return (
      <div className="flex h-screen flex-col overflow-hidden bg-background">
        <Header
          hideGlobalSearch
          onSearchClick={() => setCommandPaletteOpen(true)}
          onMobileMenuClick={() => setSidebarOpen(true)}
        />
        <CommandPalette
          open={commandPaletteOpen}
          onOpenChange={setCommandPaletteOpen}
          onCreateTicket={() => navigate(sd("tickets/new"))}
          onSelectTicket={() => {}}
        />
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Desktop — sidebar hamesha dikhe (pehle jaisa) */}
          <div className="hidden shrink-0 lg:flex">
            <Sidebar {...sidebarProps} />
          </div>

          {/* Compact layout — drawer sidebar (tablet + mobile) */}
          {isCompact ? (
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetContent
                side="left"
                className="w-64 max-w-[85vw] p-0 [&>button]:hidden"
              >
                <Sidebar
                  {...sidebarProps}
                  collapsed={false}
                  onNavigate={() => setSidebarOpen(false)}
                />
              </SheetContent>
            </Sheet>
          ) : null}

          <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
            {mainContent}
          </div>
        </div>
      </div>
    );
  }

  if (queue === "kanban") {
    return (
      <div className="flex h-screen min-h-0 flex-col overflow-hidden bg-background">
        <AdminKanban empId={kanbanEmpId} enableFilters />
      </div>
    );
  }

  return (
    <TicketsStreamGrid queue={queue} vm={vm} isAgentOnly={vm.isAgentOnly} />
  );
};

export default Tickets;
